import { useCallback, useSyncExternalStore } from 'react';
import { useAuth } from '../providers/AuthProvider';
import { hasPermission, unassignedNotifiesScoped, PERMISSIONS } from '../../lib/permissions';

/**
 * Assigned-contact scoping for inbound message notifications (#1471, #1541).
 *
 * `msg.received` fans out tenant-wide on the inbox WS channel (the Team
 * inbox needs it), so the bell and toast filter client-side. Everyone
 * defaults to assigned-contacts-only; users holding `notifications.team`
 * (role floor is tenant-configurable) can opt in to tenant-wide
 * notifications via the bell toggle. Unassigned-contact messages notify
 * scoped viewers too by default; a tenant setting
 * (notifications_unassigned_to_scoped) restricts them to team-wide viewers.
 */

const STORAGE_KEY = 'apex.notifications.teamWide';

function readPref(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

let teamWidePref = readPref();
const listeners = new Set<() => void>();

function subscribePref(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function setTeamWidePref(value: boolean): void {
  teamWidePref = value;
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Persistence is best-effort; the in-memory value still applies.
  }
  listeners.forEach((l) => l());
}

// Keep the pref in sync when another tab toggles it.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      teamWidePref = e.newValue === 'true';
      listeners.forEach((l) => l());
    }
  });
}

export interface AssignedInboundFilter {
  /** True when the user has opted in to tenant-wide message notifications. */
  teamWide: boolean;
  /** True when the user may toggle team-wide notifications (`notifications.team`). */
  canToggle: boolean;
  setTeamWide: (value: boolean) => void;
  /**
   * Whether an inbound message with this `assigned_rep_id` should notify the
   * current user. Team-wide viewers see everything; everyone else only sees
   * messages from contacts assigned to them.
   */
  shouldNotify: (assignedRepId: string | null | undefined) => boolean;
}

export function useAssignedInboundFilter(): AssignedInboundFilter {
  const { user, impersonationTarget } = useAuth();
  const optIn = useSyncExternalStore(subscribePref, () => teamWidePref);

  const canToggle = !!user && hasPermission(PERMISSIONS['notifications.team']);
  const teamWide = canToggle && optIn;
  // While impersonating, scope to the TARGET's assignments — `user` stays the
  // real platform-ops identity (/auth/me), which owns no contacts, so filtering
  // on it would blank the scoped view instead of reproducing the target's.
  const sub = impersonationTarget?.id ?? user?.sub;

  const shouldNotify = useCallback(
    (assignedRepId: string | null | undefined): boolean => {
      if (teamWide) return true;
      if (!assignedRepId) return unassignedNotifiesScoped();
      return !!sub && assignedRepId === sub;
    },
    [teamWide, sub],
  );

  return { teamWide, canToggle, setTeamWide: setTeamWidePref, shouldNotify };
}
